import { useState, useRef, useCallback } from 'react';
import {
  ArrowLeft, ArrowRight, Check, Plus, Trash2,
  Camera, Image as ImageIcon, X, Search,
} from 'lucide-react';
import API_BASE_URL from '../../config/api';

const MEASUREMENT_FIELDS = [
  { label: 'Bust', key: 'bust' },
  { label: 'Under Bust', key: 'under_bust' },
  { label: 'Waist', key: 'waist' },
  { label: 'Hip', key: 'hip' },
  { label: 'Shoulder', key: 'shoulder' },
  { label: 'Chest', key: 'chest' },
  { label: 'Neck', key: 'neck' },
  { label: 'Armhole', key: 'armhole' },
  { label: 'Bicep', key: 'bicep' },
  { label: 'Upper Arm', key: 'upper_arm' },
  { label: 'Wrist', key: 'wrist' },
  { label: 'Arm Length', key: 'arm_length' },
  { label: 'Sleeve Length', key: 'sleeve_length' },
  { label: 'Cross Front', key: 'cross_front' },
  { label: 'Cross Back', key: 'cross_back' },
  { label: 'Front Neck Depth', key: 'front_neck_depth' },
  { label: 'Back Neck Depth', key: 'back_neck_depth' },
  { label: 'Blouse Length', key: 'blouse_length' },
  { label: 'Kameez Length', key: 'kameez_length' },
  { label: 'Crop Top Length', key: 'crop_top_length' },
  { label: 'Kurta Length', key: 'kurta_length' },
  { label: 'Full Length (Top)', key: 'full_length_top' },
  { label: 'Salwar Length', key: 'salwar_length' },
  { label: 'Lehenga Length', key: 'lehenga_length' },
  { label: 'Skirt Length', key: 'skirt_length' },
  { label: 'Pant Length', key: 'pant_length' },
  { label: 'Thigh', key: 'thigh' },
  { label: 'Knee', key: 'knee' },
  { label: 'Calf', key: 'calf' },
  { label: 'Ankle', key: 'ankle' },
  { label: 'Inseam', key: 'inseam' },
];

const PAYMENT_MODES = [
  { value: 'cash', label: 'Cash' },
  { value: 'upi', label: 'UPI' },
  { value: 'card', label: 'Card' },
  { value: 'bank_transfer', label: 'Bank' },
];

const STEPS = ['Customer', 'Products', 'Measurements', 'Payment', 'Review'];

const OrderForm = ({ authAxios, source, event, onBack, onSaved }) => {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(null); // index of product being uploaded
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const searchTimeout = useRef(null);
  const fileInputRefs = useRef({});
  const cameraInputRefs = useRef({});

  const [form, setForm] = useState({
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    products: [],
    measurements: {},
    total_amount: '',
    payment_type: 'advance', // 'advance' or 'full'
    initial_payment: { amount: '', payment_mode: 'cash' },
    expected_delivery_date: '',
    notes: '',
  });

  const update = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  // ─── Product Handlers ─────────────────────────────────────────────
  const addProduct = () => {
    update('products', [...form.products, {
      product_id: null, name: '', price: '', qty: 1,
      size: '', color: '', customizations: '', image: null,
    }]);
  };

  const updateProduct = (i, key, val) => {
    const updated = [...form.products];
    updated[i] = { ...updated[i], [key]: val };
    update('products', updated);
  };

  const removeProduct = (i) => update('products', form.products.filter((_, idx) => idx !== i));

  const handleImageUpload = async (i, file) => {
    if (!file) return;
    try {
      setUploadingImage(i);
      const formData = new FormData();
      formData.append('image', file);
      const res = await authAxios.post('/api/order-book/upload-product-image/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      updateProduct(i, 'image', res.data.image_url);
    } catch (err) {
      console.error('Image upload error:', err);
      alert('Failed to upload image. Please try again.');
    } finally {
      setUploadingImage(null);
    }
  };

  const removeImage = (i) => updateProduct(i, 'image', null);

  // ─── Product Search ─────────────────────────────────────────────
  const handleSearch = useCallback((q) => {
    setSearchQuery(q);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (!q.trim()) { setSearchResults([]); return; }
    searchTimeout.current = setTimeout(async () => {
      try {
        setSearching(true);
        const res = await authAxios.get(`/api/order-book/products/search/?q=${encodeURIComponent(q.trim())}`);
        setSearchResults(res.data || []);
      } catch (err) {
        console.error('Product search error:', err);
      } finally {
        setSearching(false);
      }
    }, 300);
  }, [authAxios]);

  const getProductImageUrl = (img) => {
    if (!img) return null;
    if (img.startsWith('http')) return img;
    if (img.startsWith('/')) return `${API_BASE_URL}${img}`;
    return `${API_BASE_URL}/media/${img}`;
  };

  const addFromSearch = (product) => {
    const imgUrl = getProductImageUrl(product.img1);
    update('products', [...form.products, {
      product_id: product.id,
      name: product.name || '',
      price: product.Discountedprice || product.price || '',
      qty: 1,
      size: '',
      color: product.color || '',
      customizations: '',
      image: imgUrl,
    }]);
    setSearchQuery('');
    setSearchResults([]);
    setShowSearch(false);
  };

  const calcTotal = form.products.reduce((s, p) => s + (Number(p.price || 0) * Number(p.qty || 1)), 0);

  // ─── Submit ───────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!form.customer_name || !form.customer_phone) return;
    try {
      setSaving(true);
      // Filter out empty measurements
      const cleanMeasurements = {};
      Object.entries(form.measurements).forEach(([k, v]) => {
        if (v && String(v).trim()) cleanMeasurements[k] = v;
      });

      const totalAmt = Number(form.total_amount) || calcTotal;
      const payload = {
        source,
        event: event?.id || null,
        customer_name: form.customer_name,
        customer_phone: form.customer_phone,
        customer_email: form.customer_email,
        products: form.products.map(p => ({
          product_id: p.product_id, name: p.name,
          price: Number(p.price) || 0, qty: Number(p.qty) || 1,
          size: p.size, color: p.color, customizations: p.customizations,
          image: p.image || null,
        })),
        measurements: cleanMeasurements,
        total_amount: totalAmt,
        expected_delivery_date: form.expected_delivery_date || null,
        notes: form.notes,
        current_stage: 'confirmed',
      };

      // Payment handling
      if (form.payment_type === 'full' && totalAmt > 0) {
        payload.initial_payment = {
          amount: totalAmt,
          payment_mode: form.initial_payment.payment_mode,
        };
      } else if (form.payment_type === 'advance' && Number(form.initial_payment.amount) > 0) {
        payload.initial_payment = {
          amount: form.initial_payment.amount,
          payment_mode: form.initial_payment.payment_mode,
        };
      }

      await authAxios.post('/api/order-book/orders/', payload);
      onSaved();
    } catch (err) {
      console.error('Error creating order:', err);
      alert('Failed to save order. Please check all fields.');
    } finally {
      setSaving(false);
    }
  };

  const inputClass = "w-full px-4 py-3.5 bg-white border border-gray-200 rounded-xl text-base focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent";

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 mb-2">
        <button onClick={onBack} className="w-10 h-10 flex items-center justify-center hover:bg-gray-100 rounded-xl active:scale-95 transition-all">
          <ArrowLeft className="w-5 h-5 text-gray-700" />
        </button>
        <div className="flex-1">
          <h2 className="text-xl font-bold text-gray-900 tracking-tight">New Order</h2>
          {event && <span className="text-sm text-gray-500 font-medium">{event.name}</span>}
        </div>
      </div>

      {/* Step Progress */}
      <div className="flex items-center gap-1 mb-6 mt-4 overflow-x-auto pb-1">
        {STEPS.map((label, i) => (
          <div key={label} className="flex items-center">
            <button
              onClick={() => i < step && setStep(i)}
              disabled={i > step}
              className={`flex items-center gap-1.5 px-3.5 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap transition-all ${
                i === step ? 'bg-gray-900 text-white shadow-sm' :
                i < step ? 'bg-emerald-100 text-emerald-700' :
                'bg-gray-100 text-gray-400'
              }`}
            >
              {i < step && <Check className="w-3.5 h-3.5" />}
              <span className="hidden sm:inline">{label}</span>
              <span className="sm:hidden">{i + 1}</span>
            </button>
            {i < STEPS.length - 1 && <div className={`w-4 h-0.5 mx-0.5 rounded ${i < step ? 'bg-emerald-300' : 'bg-gray-200'}`} />}
          </div>
        ))}
      </div>

      {/* Form Content */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-5 shadow-sm">

        {/* ─── Step 0: Customer ─── */}
        {step === 0 && (
          <div className="space-y-5">
            <h3 className="text-lg font-bold text-gray-900">Customer Details</h3>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Name *</label>
              <input type="text" value={form.customer_name} onChange={(e) => update('customer_name', e.target.value)}
                placeholder="Customer name" autoFocus className={inputClass} />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Phone *</label>
              <input type="tel" inputMode="numeric" value={form.customer_phone} onChange={(e) => update('customer_phone', e.target.value)}
                placeholder="Phone number" className={inputClass} />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Email <span className="text-gray-400 font-normal">(optional)</span></label>
              <input type="email" value={form.customer_email} onChange={(e) => update('customer_email', e.target.value)}
                placeholder="Email address" className={inputClass} />
            </div>
          </div>
        )}

        {/* ─── Step 1: Products ─── */}
        {step === 1 && (
          <div className="space-y-5">
            <div>
              <h3 className="text-lg font-bold text-gray-900">Products</h3>
              <p className="text-sm text-gray-500 mt-1">Pick from your products or add custom items</p>
            </div>

            {/* Search + Add Buttons */}
            <div className="flex gap-2">
              <button type="button" onClick={() => { setShowSearch(!showSearch); setSearchQuery(''); setSearchResults([]); }}
                className={`flex-1 border-2 rounded-xl p-3.5 text-base font-semibold flex items-center justify-center gap-2 transition-all ${
                  showSearch ? 'border-gray-900 bg-gray-900 text-white' : 'border-dashed border-gray-200 text-gray-500 hover:text-gray-700 hover:border-gray-300 active:bg-gray-50'
                }`}>
                <Search className="w-5 h-5" /> Pick Product
              </button>
              <button type="button" onClick={addProduct}
                className="flex-1 border-2 border-dashed border-gray-200 rounded-xl p-3.5 text-gray-500 hover:text-gray-700 hover:border-gray-300 active:bg-gray-50 text-base font-semibold flex items-center justify-center gap-2 transition-all">
                <Plus className="w-5 h-5" /> Custom Item
              </button>
            </div>

            {/* Product Search Panel */}
            {showSearch && (
              <div className="bg-gray-50 rounded-2xl p-4">
                <div className="relative mb-3">
                  <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input type="text" value={searchQuery}
                    onChange={(e) => handleSearch(e.target.value)}
                    placeholder="Search your products..."
                    autoFocus
                    className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl text-base bg-white focus:outline-none focus:ring-2 focus:ring-gray-900" />
                  {searching && (
                    <div className="absolute right-3.5 top-1/2 -translate-y-1/2 w-5 h-5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                  )}
                </div>
                {searchResults.length > 0 ? (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {searchResults.map((p) => {
                      const imgUrl = getProductImageUrl(p.img1);
                      return (
                        <button key={p.id} type="button" onClick={() => addFromSearch(p)}
                          className="w-full flex items-center gap-3 bg-white rounded-xl p-3 border border-gray-100 hover:border-gray-300 hover:shadow-sm active:scale-[0.98] transition-all text-left">
                          {imgUrl ? (
                            <img src={imgUrl} alt="" className="w-14 h-14 rounded-lg object-cover flex-shrink-0 bg-gray-100" />
                          ) : (
                            <div className="w-14 h-14 rounded-lg bg-gray-200 flex items-center justify-center flex-shrink-0">
                              <ImageIcon className="w-6 h-6 text-gray-400" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-gray-900 truncate">{p.name}</div>
                            <div className="text-xs text-gray-500 mt-0.5">
                              {[p.category, p.color, p.gender].filter(Boolean).join(' · ')}
                            </div>
                          </div>
                          <div className="text-sm font-bold text-gray-900 flex-shrink-0">
                            ₹{Number(p.Discountedprice || p.price || 0).toLocaleString('en-IN')}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ) : searchQuery.trim() && !searching ? (
                  <div className="text-center py-6 text-sm text-gray-400">No products found</div>
                ) : !searchQuery.trim() ? (
                  <div className="text-center py-6 text-sm text-gray-400">Type to search your products</div>
                ) : null}
              </div>
            )}

            {/* Product Cards */}
            {form.products.map((product, i) => (
              <div key={i} className="bg-gray-50 rounded-2xl p-4">
                {/* Product Number + Delete */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 bg-gray-200 rounded-lg flex items-center justify-center text-xs font-bold text-gray-500">{i + 1}</div>
                    <span className="text-sm font-semibold text-gray-600">Product</span>
                  </div>
                  <button onClick={() => removeProduct(i)} className="w-9 h-9 flex items-center justify-center hover:bg-red-50 rounded-xl active:scale-95">
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </button>
                </div>

                {/* Photo Section */}
                <div className="mb-3">
                  {product.image ? (
                    <div className="relative w-full h-40 rounded-xl overflow-hidden bg-gray-100">
                      <img src={product.image} alt="Product" className="w-full h-full object-cover" />
                      <button onClick={() => removeImage(i)}
                        className="absolute top-2 right-2 w-8 h-8 bg-black/50 rounded-full flex items-center justify-center active:scale-95">
                        <X className="w-4 h-4 text-white" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      {/* Camera Button */}
                      <button type="button"
                        onClick={() => cameraInputRefs.current[i]?.click()}
                        disabled={uploadingImage === i}
                        className="flex-1 h-28 border-2 border-dashed border-gray-200 rounded-xl flex flex-col items-center justify-center gap-1.5 hover:border-gray-300 active:bg-gray-100 transition-all disabled:opacity-50">
                        {uploadingImage === i ? (
                          <div className="w-6 h-6 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                        ) : (
                          <>
                            <Camera className="w-6 h-6 text-gray-400" />
                            <span className="text-xs font-semibold text-gray-400">Camera</span>
                          </>
                        )}
                      </button>
                      {/* Gallery Button */}
                      <button type="button"
                        onClick={() => fileInputRefs.current[i]?.click()}
                        disabled={uploadingImage === i}
                        className="flex-1 h-28 border-2 border-dashed border-gray-200 rounded-xl flex flex-col items-center justify-center gap-1.5 hover:border-gray-300 active:bg-gray-100 transition-all disabled:opacity-50">
                        {uploadingImage === i ? (
                          <div className="w-6 h-6 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                        ) : (
                          <>
                            <ImageIcon className="w-6 h-6 text-gray-400" />
                            <span className="text-xs font-semibold text-gray-400">Gallery</span>
                          </>
                        )}
                      </button>
                      {/* Hidden file inputs */}
                      <input type="file" accept="image/*" capture="environment"
                        ref={el => cameraInputRefs.current[i] = el}
                        className="hidden"
                        onChange={(e) => handleImageUpload(i, e.target.files[0])} />
                      <input type="file" accept="image/*"
                        ref={el => fileInputRefs.current[i] = el}
                        className="hidden"
                        onChange={(e) => handleImageUpload(i, e.target.files[0])} />
                    </div>
                  )}
                </div>

                {/* Name */}
                <div className="mb-2">
                  <label className="text-xs font-medium text-gray-500 block mb-1">Product Name *</label>
                  <input type="text" value={product.name} onChange={(e) => updateProduct(i, 'name', e.target.value)}
                    placeholder="e.g. Silk Blouse, Lehenga Set"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-base bg-white" />
                </div>

                {/* Price + Qty */}
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <div>
                    <label className="text-xs font-medium text-gray-500 block mb-1">Price (₹) *</label>
                    <input type="number" inputMode="numeric" value={product.price}
                      onChange={(e) => updateProduct(i, 'price', e.target.value)}
                      placeholder="0"
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-base bg-white" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 block mb-1">Qty</label>
                    <input type="number" inputMode="numeric" value={product.qty} min="1"
                      onChange={(e) => updateProduct(i, 'qty', e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-base bg-white" />
                  </div>
                </div>

                {/* Size + Color */}
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <div>
                    <label className="text-xs font-medium text-gray-500 block mb-1">Size</label>
                    <input type="text" value={product.size} placeholder="e.g. M, 38"
                      onChange={(e) => updateProduct(i, 'size', e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-base bg-white" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 block mb-1">Color</label>
                    <input type="text" value={product.color} placeholder="Color"
                      onChange={(e) => updateProduct(i, 'color', e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-base bg-white" />
                  </div>
                </div>

                {/* Customizations */}
                <div>
                  <label className="text-xs font-medium text-gray-500 block mb-1">Notes / Customizations</label>
                  <input type="text" value={product.customizations}
                    onChange={(e) => updateProduct(i, 'customizations', e.target.value)}
                    placeholder="e.g. Green border, mirror work"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-base bg-white" />
                </div>
              </div>
            ))}

            {form.products.length > 0 && (
              <div className="text-right bg-gray-50 rounded-xl px-4 py-3">
                <span className="text-sm text-gray-500">Product Total:</span>{' '}
                <span className="text-lg font-bold text-gray-900">₹{calcTotal.toLocaleString('en-IN')}</span>
              </div>
            )}
          </div>
        )}

        {/* ─── Step 2: Measurements ─── */}
        {step === 2 && (
          <div className="space-y-5">
            <div>
              <h3 className="text-lg font-bold text-gray-900">Body Measurements</h3>
              <p className="text-sm text-gray-500 mt-1">Fill only what's needed — rest will be ignored</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {MEASUREMENT_FIELDS.map(({ label, key }) => (
                <div key={key}>
                  <label className="text-xs font-semibold text-gray-600 block mb-1.5">{label}</label>
                  <div className="relative">
                    <input
                      type="number" inputMode="decimal" step="0.5"
                      value={form.measurements[key] || ''}
                      onChange={(e) => update('measurements', { ...form.measurements, [key]: e.target.value })}
                      placeholder="—"
                      className="w-full px-3 py-3 border border-gray-200 rounded-xl text-base focus:outline-none focus:ring-2 focus:ring-gray-900 text-center bg-gray-50 focus:bg-white"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-gray-400">in</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ─── Step 3: Payment ─── */}
        {step === 3 && (
          <div className="space-y-5">
            <h3 className="text-lg font-bold text-gray-900">Payment & Delivery</h3>

            {/* Total Amount */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Total Order Amount (₹)</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-base font-medium">₹</span>
                <input type="number" inputMode="numeric"
                  value={form.total_amount || calcTotal || ''}
                  onChange={(e) => update('total_amount', e.target.value)}
                  placeholder={calcTotal > 0 ? String(calcTotal) : '0'}
                  className="w-full pl-9 pr-4 py-3.5 border border-gray-200 rounded-xl text-lg font-semibold focus:outline-none focus:ring-2 focus:ring-gray-900" />
              </div>
              {calcTotal > 0 && !form.total_amount && (
                <p className="text-xs text-gray-400 mt-1.5 font-medium">Auto-calculated from products</p>
              )}
            </div>

            {/* Payment Type Toggle */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Payment Status</label>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={() => update('payment_type', 'advance')}
                  className={`px-4 py-3.5 rounded-xl text-sm font-semibold border-2 transition-all ${
                    form.payment_type === 'advance' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200'
                  }`}>
                  Advance Paid
                </button>
                <button type="button" onClick={() => update('payment_type', 'full')}
                  className={`px-4 py-3.5 rounded-xl text-sm font-semibold border-2 transition-all ${
                    form.payment_type === 'full' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-gray-600 border-gray-200'
                  }`}>
                  Full Paid
                </button>
              </div>
            </div>

            {/* Advance Amount — only if advance selected */}
            {form.payment_type === 'advance' && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Advance Amount (₹)</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">₹</span>
                  <input type="number" inputMode="numeric" value={form.initial_payment.amount}
                    onChange={(e) => update('initial_payment', { ...form.initial_payment, amount: e.target.value })}
                    placeholder="0"
                    className="w-full pl-9 pr-4 py-3.5 border border-gray-200 rounded-xl text-base font-semibold focus:outline-none focus:ring-2 focus:ring-gray-900" />
                </div>
              </div>
            )}

            {/* Payment Method */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Payment Method</label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {PAYMENT_MODES.map(m => (
                  <button key={m.value} type="button"
                    onClick={() => update('initial_payment', { ...form.initial_payment, payment_mode: m.value })}
                    className={`px-2 py-3 rounded-xl text-sm font-semibold border-2 transition-all ${
                      form.initial_payment.payment_mode === m.value
                        ? 'bg-gray-900 text-white border-gray-900'
                        : 'bg-white text-gray-600 border-gray-200'
                    }`}>
                    {m.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Expected Delivery */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Expected Delivery Date</label>
              <input type="date" value={form.expected_delivery_date}
                onChange={(e) => update('expected_delivery_date', e.target.value)}
                className={inputClass} />
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Notes <span className="text-gray-400 font-normal">(optional)</span></label>
              <textarea value={form.notes} onChange={(e) => update('notes', e.target.value)}
                placeholder="Any special instructions..."
                rows={2} className={`${inputClass} resize-none`} />
            </div>
          </div>
        )}

        {/* ─── Step 4: Review ─── */}
        {step === 4 && (
          <div className="space-y-1">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Order Summary</h3>

            <div className="divide-y divide-gray-100">
              {/* Customer */}
              <div className="py-3.5 flex justify-between items-center">
                <span className="text-sm text-gray-500 font-medium">Customer</span>
                <span className="text-base font-semibold text-gray-900">{form.customer_name}</span>
              </div>
              <div className="py-3.5 flex justify-between items-center">
                <span className="text-sm text-gray-500 font-medium">Phone</span>
                <span className="text-base text-gray-900">{form.customer_phone}</span>
              </div>

              {/* Products */}
              {form.products.length > 0 && (
                <div className="py-3.5">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-gray-500 font-medium">Products</span>
                    <span className="text-sm text-gray-500">{form.products.length} items</span>
                  </div>
                  {form.products.map((p, i) => (
                    <div key={i} className="flex items-center gap-3 py-2 pl-1">
                      {p.image && (
                        <img src={p.image} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <span className="text-sm text-gray-700 truncate block">{p.name || `Item ${i + 1}`}</span>
                        {(p.size || p.color) && (
                          <span className="text-xs text-gray-400">
                            {[p.size, p.color].filter(Boolean).join(' / ')}
                          </span>
                        )}
                      </div>
                      <span className="text-sm font-medium text-gray-800 flex-shrink-0">
                        {p.qty > 1 && `${p.qty} × `}₹{Number(p.price || 0).toLocaleString('en-IN')}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Total */}
              <div className="py-3.5 flex justify-between items-center">
                <span className="text-base font-bold text-gray-900">Total</span>
                <span className="text-xl font-bold text-gray-900">₹{(Number(form.total_amount) || calcTotal).toLocaleString('en-IN')}</span>
              </div>

              {/* Payment */}
              {(form.payment_type === 'full' || Number(form.initial_payment.amount) > 0) && (
                <div className="py-3.5 flex justify-between items-center">
                  <span className="text-sm text-gray-500 font-medium">
                    {form.payment_type === 'full' ? 'Full Payment' : 'Advance'} ({form.initial_payment.payment_mode})
                  </span>
                  <span className="text-base font-semibold text-emerald-600">
                    ₹{(form.payment_type === 'full'
                      ? (Number(form.total_amount) || calcTotal)
                      : Number(form.initial_payment.amount)
                    ).toLocaleString('en-IN')}
                  </span>
                </div>
              )}

              {/* Balance */}
              {form.payment_type === 'advance' && Number(form.initial_payment.amount) > 0 && (
                <div className="py-3.5 flex justify-between items-center">
                  <span className="text-sm text-gray-500 font-medium">Balance Due</span>
                  <span className="text-base font-semibold text-amber-600">
                    ₹{((Number(form.total_amount) || calcTotal) - Number(form.initial_payment.amount)).toLocaleString('en-IN')}
                  </span>
                </div>
              )}

              {/* Measurements */}
              {Object.entries(form.measurements).filter(([, v]) => v && String(v).trim()).length > 0 && (
                <div className="py-3.5">
                  <span className="text-sm text-gray-500 font-medium block mb-2">Measurements</span>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(form.measurements).filter(([, v]) => v && String(v).trim()).map(([k, v]) => (
                      <span key={k} className="text-sm bg-gray-100 px-3 py-1.5 rounded-full font-medium text-gray-700">
                        {k.replace(/_/g, ' ')}: {v}"
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Delivery */}
              {form.expected_delivery_date && (
                <div className="py-3.5 flex justify-between items-center">
                  <span className="text-sm text-gray-500 font-medium">Expected Delivery</span>
                  <span className="text-base text-gray-900">{new Date(form.expected_delivery_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                </div>
              )}

              {form.notes && (
                <div className="py-3.5">
                  <span className="text-sm text-gray-500 font-medium block mb-1">Notes</span>
                  <span className="text-sm text-gray-700">{form.notes}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Sticky Bottom Navigation */}
      <div className="sticky bottom-0 bg-gray-50 pt-2 pb-4 -mx-1 px-1">
        <div className="flex gap-3">
          {step > 0 && (
            <button onClick={() => setStep(step - 1)}
              className="flex-1 px-4 py-4 bg-white border-2 border-gray-200 text-gray-700 rounded-xl text-base font-semibold hover:bg-gray-50 active:scale-95 transition-all flex items-center justify-center gap-2">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
          )}
          {step < STEPS.length - 1 ? (
            <button
              onClick={() => setStep(step + 1)}
              disabled={step === 0 && (!form.customer_name || !form.customer_phone)}
              className="flex-1 px-4 py-4 bg-gray-900 text-white rounded-xl text-base font-semibold hover:bg-gray-800 disabled:opacity-40 active:scale-95 transition-all flex items-center justify-center gap-2 shadow-sm">
              Next <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button onClick={handleSubmit}
              disabled={saving || !form.customer_name || !form.customer_phone}
              className="flex-1 px-4 py-4 bg-gray-900 text-white rounded-xl text-base font-semibold hover:bg-gray-800 disabled:opacity-40 active:scale-95 transition-all flex items-center justify-center gap-2 shadow-sm">
              {saving ? (
                <><div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Saving...</>
              ) : (
                <><Check className="w-5 h-5" /> Save Order</>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderForm;
